import React, { ReactNode } from 'react';

import AnimateHeight from 'react-animate-height';

import { CommentAction as AkCommentAction } from '@atlaskit/comment';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { NextEditorPlugin } from '@atlaskit/editor-common/types';
import { ExtensionProvidersProp } from '@atlaskit/editor-core/editor';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { Text } from '@atlaskit/primitives';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import {
  FabricComment,
  ApiCommentOrPlaceholder,
  ApiToFabricCommentProps,
} from 'src/components/conversation-provider/types';
import { apiToFabricComment } from 'src/components/conversation-provider/utils';
import {
  apiCommentId,
  toConversationId,
} from 'src/components/conversation-provider/utils/to-conversation';
import { Editor } from 'src/components/editor/Editor';
import { ConversationsContextInterface } from 'src/contexts/conversations-context';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';

import { Comment } from '../components/Comment';
import CommentContainer from '../containers/Comment';
import {
  CreateAnalyticsEvent,
  actionSubjectIds,
  fireEvent,
  trackEventActions,
  eventTypes,
} from '../internal/analytics';
import { getCurrentUserRole } from '../utils/get-current-user-role';

import { CommentThread } from './comment-tree';
import { ConversationHeader } from './ConversationHeader';
import * as styles from './styles';
import {
  SendAnalyticsEvent,
  SuccessHandler,
  Resolution,
  ConversationResolution,
} from './types';

export interface Props {
  id?: string;
  localId: string;
  /* Optionally restricts visual nesting of comments to a maximum level. */
  maxCommentNesting?: number;
  onAddComment?: (
    conversationId: string,
    parentId: number | null,
    value: any,
    pending?: boolean,
    onSuccess?: SuccessHandler
  ) => void;
  onUpdateComment?: (
    commentId: number,
    value: any,
    onSuccess?: SuccessHandler
  ) => void;
  onDeleteComment?: (commentId: number, onSuccess?: SuccessHandler) => void;
  onResolveComment?: (
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) => void;
  onReopenComment?: (
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) => void;
  onRevertComment?: (conversationId: string, commentId: number) => void;
  onCancel?: () => void;
  onHighlightComment?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) => void;
  onEditorOpen?: () => void;
  onEditorClose?: () => void;
  onEditorChange?: (
    isLocal: boolean,
    value: any,
    conversationId: string,
    commentId: number | undefined,
    meta: any,
    objectId: string,
    containerId?: string
  ) => void;

  // Provider
  dataProviders?: ProviderFactory;

  // Event Hooks
  onUserClick?: (user: FabricUser) => void;
  onCommentPermalinkClick?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) => void;

  // Editor
  /**
   * This controls a whether a reply can have the mention of the person being replied to.
   * It is false on global conversations on the commit and PR page.
   */
  renderCommentWithReplyMention?: boolean;
  /**
   * This is a ref for where the editor attaches popup menus
   * See https://bitbucket.org/atlassian/atlassian-frontend/src/40a79bf74a139d0934a7e3200782c115c3aaa8c9/packages/editor/editor-core/src/types/editor-props.ts#lines-150
   */
  popupsMountPoint?: HTMLElement | null;

  objectId: string;
  containerId?: string;

  placeholder?: string;
  loginLinkText?: string;
  disableScrollTo?: boolean;
  allowHelpButton?: boolean;

  portal?: HTMLElement;

  renderAdditionalCommentActions?: (
    CommentAction: typeof AkCommentAction,
    comment: FabricComment
  ) => JSX.Element[];
  renderAfterComment?: (comment: FabricComment) => JSX.Element;
  showDeleteCommentDialog?: boolean;
  renderAuthorAvatar?: (user?: FabricUser) => ReactNode;
  renderAuthorName?: (user?: FabricUser) => ReactNode;
  canCollapseThread?: boolean;
  canResolveThread?: boolean;
  commentThreads: CommentThread[];
  conversationsContext: ConversationsContextInterface;

  conversationId: string;
  showBeforeUnloadWarning?: boolean;
  loginLink?: string;

  // Dispatch
  onCreateConversation?: (
    localId: string,
    value: any,
    meta: any,
    objectId: string,
    containerId?: string,
    pending?: boolean,
    onSuccess?: SuccessHandler
  ) => void;

  isExpanded?: boolean;
  meta?: {
    [key: string]: any;
  };
  createAnalyticsEvent: CreateAnalyticsEvent;

  canModerateComments?: boolean;
  extensionProviders?: ExtensionProvidersProp;
  codeSuggestionsExtensionHandler?: (
    commentId: number,
    apiComment: ApiCommentOrPlaceholder | undefined
  ) => any;
  editorPlugins?: [NextEditorPlugin<any>, boolean][];
  getSuggestions?: () => Record<string, string>;
}

interface State {
  openEditorCount: number;
  collapsed: boolean;
  resolution?: Resolution | null;
}

export class Conversation extends React.PureComponent<Props, State> {
  declare context: any;

  static defaultProps = {
    placeholder: 'What do you want to say?',
    onEditorOpen: () => {},
    onEditorClose: () => {},
  };

  constructor(props: Props) {
    super(props);

    const resolution: Resolution | null = this.getThreadResolutionState(
      props.commentThreads
    );

    this.state = {
      openEditorCount: 0,
      collapsed: !!resolution && this.threadContainsHighlightedComment(),
      resolution,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const resolution = this.getThreadResolutionState(this.props.commentThreads);
    if (
      this.props.conversationsContext.highlightedCommentId !==
      prevProps.conversationsContext.highlightedCommentId
    ) {
      this.setCollapsedState(
        !!resolution && this.threadContainsHighlightedComment()
      );
    }
  }

  static contextType = DiffFeaturesContext;

  threadContainsHighlightedComment(): boolean {
    if (
      this.props.commentThreads === undefined ||
      this.props.commentThreads.length === 0
    ) {
      return true;
    }

    if (
      apiCommentId(this.props.commentThreads[0].topComment).toString() !==
      this.props.conversationsContext.highlightedCommentId?.toString()
    ) {
      return true;
    }

    return false;
  }

  getThreadResolutionState(threads?: CommentThread[]): Resolution | null {
    if (!threads || threads.length === 0) {
      return null;
    }
    return threads[0].topComment.resolution || null;
  }

  /*
    TODO: Remove me when editor is instrumented
    Only use this method when instrumenting something that isn't instrumented itself (like Editor)
    Once editor is instrumented use the analyticsEvent passed in by editor instead.

    nestedDepth is always 0 when using the save handlers in this file.
    Because a new comment created on the conversation itself is always going to be the top comment.

    @deprecated
  */
  sendEditorAnalyticsEvent: SendAnalyticsEvent = eventData => {
    const { createAnalyticsEvent, objectId, containerId } = this.props;

    const analyticsEvent = createAnalyticsEvent({
      actionSubject: eventData.attributes?.isCodeReviewer
        ? 'code-reviewer'
        : 'editor',
      action: 'clicked',
    });

    fireEvent(analyticsEvent, { objectId, containerId, ...eventData });
  };

  collapseConversation = (): void => {
    const {
      conversationsContext: {
        eventHandlers,
        pullRequest,
        workspacePaid,
        repoId,
        user,
      },
    } = this.props;

    this.setState({
      collapsed: true,
    });

    eventHandlers?.onCommentExpandCollapse({
      action: 'collapse',
      resolved: !!this.state.resolution,
      prStatus: pullRequest?.state,
      prId: pullRequest?.id,
      paid: workspacePaid,
      repoId,
      role: getCurrentUserRole(user, pullRequest),
    });
  };

  expandConversation = (): void => {
    const {
      conversationsContext: {
        eventHandlers,
        pullRequest,
        workspacePaid,
        repoId,
        user,
      },
    } = this.props;

    this.setState({
      collapsed: false,
    });

    eventHandlers?.onCommentExpandCollapse({
      action: 'expand',
      resolved: !!this.state.resolution,
      prStatus: pullRequest?.state,
      prId: pullRequest?.id,
      paid: workspacePaid,
      repoId,
      role: getCurrentUserRole(user, pullRequest),
    });
  };

  setCollapsedState = (collapsed: boolean): void => {
    this.setState({
      collapsed,
    });
  };

  setResolution = (resolution: Resolution | null): void => {
    this.setState({
      resolution,
    });
    this.setCollapsedState(!!resolution);
  };

  private renderComments() {
    const {
      conversationId,
      conversationsContext,
      onAddComment,
      onUpdateComment,
      onDeleteComment,
      onRevertComment,
      onResolveComment,
      onReopenComment,
      onUserClick,
      onCancel,
      dataProviders,
      objectId,
      containerId,
      disableScrollTo,
      allowHelpButton,
      portal,
      canModerateComments,
      renderAdditionalCommentActions,
      renderAfterComment,
      renderAuthorAvatar,
      renderAuthorName,
      maxCommentNesting,
      showDeleteCommentDialog,
      showBeforeUnloadWarning,
      canCollapseThread,
      canResolveThread,
      popupsMountPoint,
      renderCommentWithReplyMention,
      extensionProviders,
      codeSuggestionsExtensionHandler,
      editorPlugins,
      getSuggestions,
    } = this.props;

    const { user, highlightedCommentId, highlightCommentThread } =
      conversationsContext;

    // codeSuggestionsExtensionHandler is only available when the diff context has code suggestions
    // and the comment is an inline comment and the view mode ff is enabled
    const shouldParseCodeSuggestions = Boolean(codeSuggestionsExtensionHandler);
    const additionalParseOptions = { shouldParseCodeSuggestions };

    return (this.props.commentThreads || []).map(commentThread => {
      const isHighlighted =
        highlightedCommentId === apiCommentId(commentThread.topComment) &&
        highlightCommentThread === false;
      const apiToFabricCommentProps: ApiToFabricCommentProps = {
        comment: commentThread.topComment,
        conversationId: toConversationId(commentThread.topComment),
        additionalParseOptions,
      };
      const comment = apiToFabricComment(apiToFabricCommentProps);
      return (
        <styles.CommentWrapper key={comment.commentId}>
          {/* In order to facilitate smooth animation of collapsing, non-resolved
          comments, we put both the "header" (i.e., the collapsed version of the
          comment) and the comment itself in the same grid area. This causes them
          to occupy the same space and the layout engine uses the largest of the
          two nodes for layout. AnimateHeight is applied to both elements, mostly
          obscuring the transition.

          If the comment is resolved, we want to show both the header and the
          conversation, so we put the header in its own grid area. */}
          <div
            style={{
              display: 'grid',
              gridTemplateAreas: '"header" "conversation"',
            }}
          >
            {canCollapseThread && (
              <div
                style={{
                  gridArea: this.state.resolution ? 'header' : 'conversation',
                }}
              >
                <ConversationHeader
                  resolution={this.state.resolution}
                  collapsed={this.state.collapsed}
                  user={comment.createdBy}
                  renderAuthorAvatar={renderAuthorAvatar}
                  renderAuthorName={renderAuthorName}
                  createdAt={comment.createdAt}
                />
              </div>
            )}
            <AnimateHeight
              duration={250}
              animateOpacity
              height={this.state.collapsed ? 0 : 'auto'}
              style={{ gridArea: 'conversation' }}
            >
              {canCollapseThread && this.state.resolution && (
                <styles.DividingLine />
              )}
              <CommentContainer
                key={comment.commentId}
                conversationId={conversationId}
                comment={comment}
                user={user}
                onAddComment={onAddComment}
                onUpdateComment={onUpdateComment}
                onDeleteComment={onDeleteComment}
                onRevertComment={onRevertComment}
                onResolveComment={onResolveComment}
                onReopenComment={onReopenComment}
                onEditorOpen={this.onEditorOpen}
                onEditorClose={this.onEditorClose}
                onEditorChange={this.handleEditorChange}
                onHighlightComment={this.onHighlightComment}
                setConversationResolution={this.setResolution}
                onRetry={this.onRetry(comment.document, comment.pending)}
                onCancel={onCancel}
                onUserClick={onUserClick}
                dataProviders={dataProviders}
                nestedDepth={0} // This wrapper starts the depth at zero; recursions increase the depth
                renderComment={props => (
                  <Comment
                    {...props}
                    canModerateComment={canModerateComments}
                    canResolveThread={canResolveThread}
                    renderAdditionalCommentActions={
                      renderAdditionalCommentActions
                    }
                    renderAfterComment={renderAfterComment}
                    renderAuthorAvatar={renderAuthorAvatar}
                    renderAuthorName={renderAuthorName}
                    popupsMountPoint={popupsMountPoint}
                    renderCommentWithReplyMention={
                      renderCommentWithReplyMention
                    }
                    isHighlighted={isHighlighted}
                    conversationsContext={conversationsContext}
                    collapseThread={this.collapseConversation}
                    extensionProviders={extensionProviders}
                    codeSuggestionsExtensionHandler={
                      codeSuggestionsExtensionHandler
                    }
                    editorPlugins={editorPlugins}
                    getSuggestions={getSuggestions}
                  />
                )}
                commentThread={commentThread}
                maxCommentNesting={maxCommentNesting}
                objectId={objectId}
                containerId={containerId}
                disableScrollTo={disableScrollTo}
                sendAnalyticsEvent={this.sendEditorAnalyticsEvent}
                allowHelpButton={allowHelpButton}
                portal={portal}
                showDeleteCommentDialog={showDeleteCommentDialog}
                showBeforeUnloadWarning={showBeforeUnloadWarning}
                conversationsContext={conversationsContext}
              />
            </AnimateHeight>
          </div>
        </styles.CommentWrapper>
      );
    });
  }

  private onCancel = () => {
    this.sendEditorAnalyticsEvent({
      actionSubjectId: actionSubjectIds.cancelButton,
    });

    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  private renderConversationsEditor() {
    const {
      isExpanded,
      meta,
      placeholder,
      loginLinkText,
      disableScrollTo,
      allowHelpButton,
      showBeforeUnloadWarning,
      objectId,
      loginLink,
      popupsMountPoint,
      extensionProviders,
      editorPlugins,
      conversationsContext: { user },
      getSuggestions,
    } = this.props;
    const isInline = !!meta;
    const hasConversation = this.props.commentThreads.length > 0;
    const canReply = !!user && (!isInline || (isExpanded && !hasConversation));

    const path = meta?.path || 0;
    const fromLine = meta?.from || 0;
    const toLine = meta?.to || 0;
    const draftLocalStorageKey = `${objectId}-${path}-${fromLine}-${toLine}`;

    // true for pull request page only
    const { multiCommentReview } = this.context;

    if (canReply) {
      return (
        <Editor
          isExpanded={isExpanded}
          onSave={this.onSave}
          onCancel={this.onCancel}
          onOpen={this.onEditorOpen}
          onClose={this.onEditorClose}
          onChange={this.handleEditorChange}
          user={user}
          placeholder={placeholder}
          disableScrollTo={disableScrollTo}
          allowHelpButton={allowHelpButton}
          showBeforeUnloadWarning={showBeforeUnloadWarning}
          draftLocalStorageKey={draftLocalStorageKey}
          multiCommentReview={multiCommentReview}
          popupsMountPoint={popupsMountPoint || undefined}
          extensionProviders={extensionProviders}
          editorPlugins={editorPlugins}
          filePath={path}
          getSuggestions={getSuggestions}
        />
      );
    }

    if (!user) {
      return <a href={loginLink}>{loginLinkText}</a>;
    }

    return null;
  }

  private onRetry =
    (document: any, pending?: boolean) => (commentLocalId?: string) => {
      this.sendEditorAnalyticsEvent({
        actionSubjectId: actionSubjectIds.retryFailedRequestButton,
      });
      this.onSave(document, pending, commentLocalId, true);
    };

  private onSave = (
    value: any,
    pending?: boolean,
    commentLocalId?: string,
    retry?: boolean
  ) => {
    const {
      objectId,
      containerId,
      id,
      localId,
      meta,
      onAddComment,
      onCreateConversation,
      conversationId: propsConversationId,
      conversationsContext: {
        eventHandlers,
        pullRequest,
        repoId,
        activityFeedVersion,
      },
    } = this.props;

    if (!retry) {
      this.sendEditorAnalyticsEvent({
        actionSubjectId: actionSubjectIds.saveButton,
      });
    }

    if (!id && !commentLocalId && onCreateConversation) {
      onCreateConversation(
        localId,
        value,
        meta,
        objectId,
        containerId,
        pending,
        conversationId => {
          eventHandlers?.onCommentCreate({
            commentId: conversationId,
            repoUuid: repoId,
            pullRequestStatus: pullRequest?.state,
            pullRequestLocalId: pullRequest?.id,
            pending,
          });
          this.sendEditorAnalyticsEvent({
            actionSubjectId: conversationId,
            eventType: eventTypes.TRACK,
            attributes: {
              nestedDepth: 0,
              pending,
              activityFeedVersion,
              draft: pullRequest?.draft,
            },
            action: trackEventActions.created,
            actionSubject: 'comment',
          });
        }
      );
    } else if (onAddComment) {
      const conversationId = id || propsConversationId;
      // parent ID always null here (top-level comment)
      onAddComment(conversationId, null, value, pending, commentId => {
        eventHandlers?.onCommentCreate({
          commentId,
          repoUuid: repoId,
          pullRequestStatus: pullRequest?.state,
          pullRequestLocalId: pullRequest?.id,
          pending,
        });
        this.sendEditorAnalyticsEvent({
          actionSubjectId: commentId,
          action: trackEventActions.created,
          eventType: eventTypes.TRACK,
          actionSubject: 'comment',
          attributes: {
            nestedDepth: 0,
            pending,
            activityFeedVersion,
            draft: pullRequest?.draft,
          },
        });
      });
    }
  };

  private onEditorClose = () => {
    if (this.state.openEditorCount > 0) {
      this.setState({
        openEditorCount: this.state.openEditorCount - 1,
      });
    }

    if (typeof this.props.onEditorClose === 'function') {
      this.props.onEditorClose();
    }
  };

  private onEditorOpen = () => {
    this.sendEditorAnalyticsEvent({
      actionSubjectId: actionSubjectIds.createCommentInput,
    });

    this.setState({
      openEditorCount: this.state.openEditorCount + 1,
    });

    if (typeof this.props.onEditorOpen === 'function') {
      this.props.onEditorOpen();
    }
  };

  private onHighlightComment = (
    event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) => {
    if (typeof this.props.onHighlightComment === 'function') {
      this.props.onHighlightComment(event, commentId);
      if (typeof this.props.onCommentPermalinkClick === 'function') {
        this.props.onCommentPermalinkClick(event, commentId);
      }
    }
  };

  private handleEditorChange = (value: any, commentId?: number) => {
    const { id, localId, onEditorChange, meta, objectId, containerId } =
      this.props;

    if (onEditorChange) {
      const isLocal = !id;
      onEditorChange(
        isLocal,
        value,
        localId,
        commentId,
        meta,
        objectId,
        containerId
      );
    }
  };

  private renderMultiLineHeader() {
    const { commentThreads } = this.props;
    /* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
    // TODO remove this linter check when we can convince the linter the below colors are accurate
    if (!commentThreads || commentThreads.length === 0) {
      return <></>;
    }
    const start_to = commentThreads[0].topComment.inline?.start_to;
    const start_from = commentThreads[0].topComment.inline?.start_from;
    const to = commentThreads[0].topComment.inline?.to;
    const from = commentThreads[0].topComment.inline?.from;
    // TODO currently we assume that all to lines are green and all from lines are red
    // but some of these may be context lines and we don't detect that right now
    if (start_to && to) {
      return (
        <>
          Lines{' '}
          <Text weight="semibold" color="color.text.accent.green">
            +{start_to}
          </Text>{' '}
          to{' '}
          <Text weight="semibold" color="color.text.accent.green">
            +{to}
          </Text>
        </>
      );
    }
    if (start_from && from) {
      return (
        <>
          Lines{' '}
          <Text weight="semibold" color="color.text.accent.red">
            -{start_from}
          </Text>{' '}
          to{' '}
          <Text weight="semibold" color="color.text.accent.red">
            -{from}
          </Text>
        </>
      );
    }
    if (start_to && from) {
      return (
        <>
          Lines{' '}
          <Text weight="semibold" color="color.text.accent.green">
            +{start_to}
          </Text>{' '}
          to{' '}
          <Text weight="semibold" color="color.text.accent.red">
            -{from}
          </Text>
        </>
      );
    }
    if (start_from && to) {
      return (
        <>
          Lines{' '}
          <Text weight="semibold" color="color.text.accent.red">
            -{start_from}
          </Text>{' '}
          to{' '}
          <Text weight="semibold" color="color.text.accent.green">
            +{to}
          </Text>
        </>
      );
    }
    return <></>;
  }

  private renderConversationHeader() {
    const { canCollapseThread } = this.props;

    const multi_line_header = this.renderMultiLineHeader();

    if (!canCollapseThread) {
      // return out with empty component if the thread isn't collapsable.
      return <>{multi_line_header}</>;
    }
    if (this.state.collapsed) {
      return (
        <styles.CollapseExpandButton onClick={this.expandConversation}>
          {multi_line_header}
          <ChevronRightIcon label="expand" />
        </styles.CollapseExpandButton>
      );
    } else {
      return (
        <styles.CollapseExpandButton onClick={this.collapseConversation}>
          {multi_line_header}
          <ChevronDownIcon label="collapse" />
        </styles.CollapseExpandButton>
      );
    }
  }

  render() {
    return (
      <styles.ConversationWrapper>
        {this.renderConversationHeader()}
        {this.renderComments()}
        {this.renderConversationsEditor()}
      </styles.ConversationWrapper>
    );
  }
}
